import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Pay to book (public booking page). The anon-safe RPCs and the
 * create-booking-checkout edge function from migration
 * 20261102022214_pay_to_book.sql.
 */

type Rpc = {
  rpc(name: string, args: Record<string, unknown>): Promise<{ data: unknown; error: { message?: string } | null }>;
};
const rpc = (name: string, args: Record<string, unknown>) => (supabase as unknown as Rpc).rpc(name, args);

/** Whether this business can take card payments when customers book. */
export function useBookingCardPayments(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['booking-payment-options', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<{ card: boolean }> => {
      const { data, error } = await rpc('get_org_booking_payment_options', { _organization_id: orgId });
      if (error) {
        // Never block booking on this: no card option, cash/PayPal still work.
        console.error('Could not load card payment availability', error);
        return { card: false };
      }
      const value = data as { card?: unknown } | null;
      return { card: value?.card === true };
    },
  });
}

export interface BookingPaymentStatus {
  bookingId: string;
  status: string;
  paymentStatus: string | null;
  paymentMethod: string | null;
  total: number | null;
  dueNow: number | null;
  amountPaid: number | null;
  currency: string | null;
  holdExpiresAt: string | null;
  isRepeat: boolean;
  scheduledDate: string | null;
  startTime: string | null;
  cashAllowed: boolean;
}

/** True while the webhook hasn't confirmed the payment yet. */
export function isStillAwaitingPayment(status: BookingPaymentStatus | null | undefined): boolean {
  return status?.status === 'awaiting_payment';
}

/**
 * Where a held booking stands, for the page Stripe sends them back to. Polls
 * every 2s while `poll` is on and the payment hasn't been confirmed yet.
 */
export function useBookingPaymentStatus(
  bookingId: string | null | undefined,
  token: string | null | undefined,
  { poll = false }: { poll?: boolean } = {},
) {
  return useQuery({
    queryKey: ['booking-payment-status', bookingId],
    enabled: Boolean(bookingId && token),
    queryFn: async (): Promise<BookingPaymentStatus | null> => {
      const { data, error } = await rpc('get_booking_payment_status', { _booking_id: bookingId, _token: token });
      if (error) throw new Error(error.message ?? 'Could not check your booking');
      return (data as BookingPaymentStatus | null) ?? null;
    },
    refetchInterval: (query) => {
      if (!poll) return false;
      const data = query.state.data as BookingPaymentStatus | null | undefined;
      // Stop after ~40s (20 polls) or as soon as it's settled.
      if (query.state.dataUpdateCount > 20) return false;
      return data && !isStillAwaitingPayment(data) ? false : 2000;
    },
  });
}

async function functionErrorMessage(error: unknown, fallback: string): Promise<string> {
  const context = (error as { context?: { json?: () => Promise<unknown> } } | null)?.context;
  if (context && typeof context.json === 'function') {
    try {
      const body = (await context.json()) as { error?: unknown };
      if (typeof body?.error === 'string' && body.error) return body.error;
    } catch {
      // not JSON
    }
  }
  return fallback;
}

export interface StartCheckoutInput {
  bookingId: string;
  token: string;
  returnPath: string;
}

/** Open Stripe Checkout for the customer's own held booking. Resolves to its URL. */
export async function startBookingCheckout(input: StartCheckoutInput): Promise<{ url: string; amount?: number }> {
  const { data, error } = await supabase.functions.invoke('create-booking-checkout', { body: input });
  if (error) {
    throw new Error(await functionErrorMessage(error, 'We couldn’t open the payment page. Please try again.'));
  }
  const result = data as { url?: string; amount?: number; error?: string } | null;
  if (!result?.url) throw new Error(result?.error || 'We couldn’t open the payment page. Please try again.');
  return { url: result.url, amount: result.amount };
}

export function useStartBookingCheckout() {
  return useMutation({ mutationFn: startBookingCheckout });
}

/** Cancelled at Stripe and the business takes cash: book it as cash instead. */
export function useSwitchBookingToCash() {
  return useMutation({
    mutationFn: async ({ bookingId, token }: { bookingId: string; token: string }) => {
      const { data, error } = await rpc('switch_booking_to_cash', { _booking_id: bookingId, _token: token });
      if (error) throw new Error(error.message || 'We couldn’t switch that to cash. Please try again.');
      return data as { bookingId: string; status: string; confirmation: 'instant' | 'request'; seriesId: string | null };
    },
  });
}
